import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  db,
  collection,
  onSnapshot,
  getDocs,
} from '../firebase';
import { todoAssigneeIds } from '../lib/todoAssignees';
import { normalizeItemColor, eventColorHex } from '../lib/itemColors';
import {
  formatEventWhen,
  formatRecurrenceLine,
  formatCalendarReminderSummary,
  eventSortKey,
} from '../lib/calendarEvent';
import { formatTodoReminderDisplay } from '../lib/todoReminders';

const SECTIONS = [
  { id: 'todos', labelKey: 'adminGlobalTodos' },
  { id: 'calendar', labelKey: 'adminGlobalCalendar' },
  { id: 'plans', labelKey: 'adminGlobalPlans' },
];

function GlobalOverviewTab() {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState('todos');
  const [todos, setTodos] = useState([]);
  const [events, setEvents] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [planItems, setPlanItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterUserId, setFilterUserId] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const u1 = onSnapshot(collection(db, 'todos'), (snap) => {
      setTodos(snap.docs.map((d) => ({ _id: d.id, ...d.data() })));
    });
    const u2 = onSnapshot(collection(db, 'calendarEvents'), (snap) => {
      setEvents(snap.docs.map((d) => ({ _id: d.id, ...d.data() })));
    });
    const u3 = onSnapshot(collection(db, 'sprints'), (snap) => {
      setSprints(snap.docs.map((d) => ({ _id: d.id, ...d.data() })));
    });
    const u4 = onSnapshot(collection(db, 'planItems'), (snap) => {
      setPlanItems(snap.docs.map((d) => ({ _id: d.id, ...d.data() })));
    });
    (async () => {
      const us = await getDocs(collection(db, 'users'));
      setUsers(us.docs.map((d) => ({ _id: d.id, ...d.data() })));
    })();
    return () => {
      u1();
      u2();
      u3();
      u4();
    };
  }, []);

  const userById = useMemo(() => {
    const m = {};
    for (const u of users) m[u._id] = u;
    return m;
  }, [users]);

  const ownerLabel = (uid) => {
    if (!uid) return t('adminGlobalUnknownOwner');
    const u = userById[uid];
    return u?.name || u?.email || uid;
  };

  const assigneeNames = (todo) =>
    todoAssigneeIds(todo)
      .map((id) => userById[id]?.name)
      .filter(Boolean)
      .join(', ');

  const participantsLabel = (ev) =>
    (ev.participants || [])
      .map((pid) => userById[pid]?.name)
      .filter(Boolean)
      .join(', ');

  const searchQuery = search.trim().toLowerCase();

  const filteredTodos = useMemo(() => {
    const q = searchQuery;
    const match = (text) => !q || String(text || '').toLowerCase().includes(q);
    const rows = todos.filter((t0) => {
      if (filterUserId && t0.userId !== filterUserId) return false;
      if (!match(`${t0.text || ''} ${t0.description || ''}`)) return false;
      return true;
    });
    rows.sort((a, b) => {
      const da = a.deadlineAt?.toDate?.()?.getTime() ?? Number.POSITIVE_INFINITY;
      const dbb = b.deadlineAt?.toDate?.()?.getTime() ?? Number.POSITIVE_INFINITY;
      if (da !== dbb) return da - dbb;
      return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
    });
    return rows;
  }, [todos, filterUserId, searchQuery]);

  const filteredEvents = useMemo(() => {
    const q = searchQuery;
    const match = (text) => !q || String(text || '').toLowerCase().includes(q);
    const rows = events.filter((ev) => {
      if (filterUserId && ev.userId !== filterUserId) return false;
      if (!match(`${ev.title || ''} ${ev.notes || ''} ${ev.location || ''}`)) return false;
      return true;
    });
    rows.sort((a, b) => eventSortKey(a) - eventSortKey(b));
    return rows;
  }, [events, filterUserId, searchQuery]);

  const itemsBySprint = useMemo(() => {
    const m = {};
    for (const it of planItems) {
      if (!m[it.sprintId]) m[it.sprintId] = [];
      m[it.sprintId].push(it);
    }
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    return m;
  }, [planItems]);

  const sprintsByUser = useMemo(() => {
    const q = searchQuery;
    const match = (text) => !q || String(text || '').toLowerCase().includes(q);
    const base = sprints.filter((sp) => {
      if (filterUserId && sp.userId !== filterUserId) return false;
      if (!match(`${sp.title || ''} ${sp.description || ''}`)) return false;
      return true;
    });
    base.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const m = new Map();
    for (const sp of base) {
      const key = sp.userId || '__none__';
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(sp);
    }
    return m;
  }, [sprints, filterUserId, searchQuery]);

  const fmtDate = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString(i18n.language);
  };

  const fmtDateTime = (ts) => (ts?.toDate ? ts.toDate().toLocaleString(i18n.language) : '');

  return (
    <div>
      <h1 className="haydi-page-title">{t('adminGlobalOverview')}</h1>
      <p className="haydi-muted" style={{ marginTop: '-0.4rem', marginBottom: '1rem' }}>
        {t('adminGlobalOverviewHint')}
      </p>

      <div className="haydi-pill-tabs" role="tablist" style={{ marginBottom: '1rem' }}>
        {SECTIONS.map((s) => (
          <button
            type="button"
            key={s.id}
            role="tab"
            aria-selected={view === s.id}
            className={`haydi-pill-tab${view === s.id ? ' haydi-pill-tab--active' : ''}`}
            onClick={() => setView(s.id)}
          >
            {t(s.labelKey)}
          </button>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <label className="haydi-muted" style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.75rem', fontWeight: 600 }}>
          {t('adminGlobalFilterUser')}
          <select
            className="haydi-select haydi-input"
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
            style={{ minWidth: 200 }}
          >
            <option value="">{t('adminGlobalFilterAll')}</option>
            {users.map((u) => (
              <option key={u._id} value={u._id}>
                {u.name || u.email || u._id}
              </option>
            ))}
          </select>
        </label>
        <label className="haydi-muted" style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.75rem', fontWeight: 600, flex: 1, minWidth: 200 }}>
          {t('adminGlobalSearch')}
          <input
            type="search"
            className="haydi-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('adminGlobalSearchPlaceholder')}
          />
        </label>
      </div>

      {view === 'todos' && (
        <section>
          <p className="haydi-muted" style={{ fontSize: '0.85rem', marginTop: 0 }}>
            {t('adminGlobalTotalCount', { count: filteredTodos.length })}
          </p>
          {filteredTodos.length === 0 && (
            <p className="haydi-muted">{t('adminGlobalEmpty')}</p>
          )}
          <ul className="haydi-todo-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {filteredTodos.map((todo) => {
              const c = normalizeItemColor(todo.color);
              const remLine = formatTodoReminderDisplay(todo, t, i18n);
              const names = assigneeNames(todo);
              return (
                <li
                  key={todo._id}
                  className={`haydi-todo-card${todo.done ? ' haydi-todo-card--done' : ''}`}
                  style={{ '--todo-color': c }}
                >
                  <div className="haydi-todo-card__body">
                    <div className="haydi-todo-card__title-row">
                      <span className="haydi-todo-card__title">{todo.text}</span>
                      <span className="haydi-chip haydi-chip--xs">
                        {t('adminGlobalOwnedBy', { name: ownerLabel(todo.userId) })}
                      </span>
                    </div>
                    {todo.description && (
                      <div className="haydi-todo-card__desc">{todo.description}</div>
                    )}
                    {(todo.deadlineAt?.toDate || remLine) && (
                      <div className="haydi-todo-card__meta-grid">
                        {todo.deadlineAt?.toDate && (
                          <div className="haydi-todo-card__pill">
                            <span className="haydi-todo-card__pill-label">{t('todoDeadline')}</span>
                            {fmtDateTime(todo.deadlineAt)}
                          </div>
                        )}
                        {remLine && (
                          <div className="haydi-todo-card__pill">
                            <span className="haydi-todo-card__pill-label">{t('todoReminderLabel')}</span>
                            {remLine}
                          </div>
                        )}
                      </div>
                    )}
                    {names && (
                      <div className="haydi-todo-card__assignees">{t('assignTo')}: {names}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {view === 'calendar' && (
        <section>
          <p className="haydi-muted" style={{ fontSize: '0.85rem', marginTop: 0 }}>
            {t('adminGlobalTotalCount', { count: filteredEvents.length })}
          </p>
          {filteredEvents.length === 0 && (
            <p className="haydi-muted">{t('adminGlobalEmpty')}</p>
          )}
          <ul className="haydi-cal-event-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {filteredEvents.map((ev) => {
              const recLine = formatRecurrenceLine(ev, t);
              const remSum = formatCalendarReminderSummary(ev, t);
              const pLabel = participantsLabel(ev);
              return (
                <li
                  key={ev._id}
                  className="haydi-cal-event-card"
                  style={{ '--ev-color': eventColorHex(ev) }}
                >
                  <div className="haydi-cal-event-card__main">
                    <div className="haydi-cal-event-card__title-row">
                      <span className="haydi-cal-event-card__title">{ev.title}</span>
                      <span className="haydi-chip haydi-chip--xs">
                        {t('adminGlobalOwnedBy', { name: ownerLabel(ev.userId) })}
                      </span>
                    </div>
                    <div className="haydi-cal-event-card__when">{formatEventWhen(ev, i18n.language)}</div>
                    {recLine && <div className="haydi-cal-event-card__rec">{recLine}</div>}
                    {ev.location && (
                      <div className="haydi-cal-event-card__meta">{t('calendarLocation')}: {ev.location}</div>
                    )}
                    {ev.notes && <div className="haydi-cal-event-card__notes">{ev.notes}</div>}
                    {remSum && (
                      <div className="haydi-cal-event-card__meta">
                        {t('calendarReminderLabel')}: {remSum}
                      </div>
                    )}
                    {(pLabel || ev.participants?.length) && (
                      <div className="haydi-cal-event-card__meta">
                        {t('participants')}: {pLabel || '—'}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {view === 'plans' && (
        <section>
          {sprintsByUser.size === 0 && (
            <p className="haydi-muted">{t('adminGlobalEmpty')}</p>
          )}
          {[...sprintsByUser.entries()].map(([ownerUid, ownerSprints]) => (
            <div key={ownerUid} style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ color: 'var(--haydi-primary)', marginBottom: '0.5rem' }}>
                {ownerLabel(ownerUid)}
                <span className="haydi-muted" style={{ fontWeight: 400, fontSize: '0.85rem', marginLeft: 8 }}>
                  {t('adminGlobalSprintCount', { count: ownerSprints.length })}
                </span>
              </h3>
              <ul className="haydi-plans-sprint-grid" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {ownerSprints.map((sp) => {
                  const items = itemsBySprint[sp._id] || [];
                  const doneCount = items.filter((it) => it.done).length;
                  const total = items.length;
                  const pct = total ? Math.round((doneCount / total) * 100) : 0;
                  return (
                    <li key={sp._id} className="haydi-sprint">
                      <div className="haydi-sprint__head">
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.35rem' }}>
                            <strong>{sp.title}</strong>
                            <span className={`haydi-badge${sp.status === 'active' ? ' haydi-badge--active' : ''}`}>
                              {t(`sprintStatus_${sp.status || 'planned'}`)}
                            </span>
                            <span className="haydi-sprint__progress">
                              {t('plansProgressLabel', { done: doneCount, total })}
                            </span>
                          </div>
                          <span className="haydi-muted" style={{ fontSize: '0.8125rem', display: 'block', marginTop: '0.25rem' }}>
                            {fmtDate(sp.startDate)} – {fmtDate(sp.endDate)}
                          </span>
                          {sp.description && (
                            <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                              {sp.description}
                            </p>
                          )}
                          <div className="haydi-sprint__progress-bar" style={{ maxWidth: 200, marginTop: '0.45rem' }}>
                            <div className="haydi-sprint__progress-fill" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                      {items.length > 0 && (
                        <div className="haydi-sprint__body">
                          <ul className="haydi-stack-sm" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {items.map((it) => (
                              <li
                                key={it._id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  padding: '0.4rem 0',
                                  borderBottom: '1px solid var(--haydi-border)',
                                }}
                              >
                                <input type="checkbox" checked={!!it.done} readOnly />
                                <span
                                  style={{
                                    textDecoration: it.done ? 'line-through' : 'none',
                                    color: it.done ? 'var(--haydi-text-muted)' : 'var(--haydi-text)',
                                  }}
                                >
                                  {it.title}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

export default GlobalOverviewTab;
